import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, Optional } from '@angular/core';
import { Dir } from '@angular/material';
import { TdCollapseAnimation } from '../common/common.module';
export class TdJsonFormatterComponent {
/**
 * Max length for property names. Any names bigger than this get trunctated.
 */
private static KEY_MAX_LENGTH: number = 30;
/**
 * Max length for preview string. Any names bigger than this get trunctated.
 */
private static PREVIEW_STRING_MAX_LENGTH: number = 80;
/**
 * Max tooltip preview elements.
 */
private static PREVIEW_LIMIT: number = 5;
private _key: string;
private _data: any;
private _children: string[];
private _open: boolean = false;
private _levelsOpen: number = 0;
/**
 * levelsOpen?: number
 * Levels opened by default when JS object is formatted and rendered.
 * @param {?} levelsOpen
 * @return {?}
 */
set levelsOpen(levelsOpen: number) {
    if (!Number.isInteger(levelsOpen)) {
      throw new Error('[levelsOpen] needs to be an integer.');
    }
    this._levelsOpen = levelsOpen;
    this._open = levelsOpen > 0;
  }
/**
 * @return {?}
 */
get levelsOpen(): number {
    return this._levelsOpen;
  }
/**
 * @return {?}
 */
get open(): boolean {
    return this._open;
  }
/**
 * key?: string
 * Tag to be displayed next to formatted object.
 * @param {?} key
 * @return {?}
 */
set key(key: string) {
    this._key = key;
  }
/**
 * @return {?}
 */
get key(): string {
    let /** @type {?} */ elipsis: string = this._key && this._key.length > TdJsonFormatterComponent.KEY_MAX_LENGTH ? '…' : '';
    return this._key ? this._key.substring(0, TdJsonFormatterComponent.KEY_MAX_LENGTH) + elipsis : this._key;
  }
/**
 * data: any
 * JS object to be formatted.
 * @param {?} data
 * @return {?}
 */
set data(data: any) {
    this._data = data;
    this.parseChildren();
  }
/**
 * @return {?}
 */
get data(): any {
    return this._data;
  }
/**
 * @return {?}
 */
get children(): string[] {
    return this._children;
  }
/**
 * @return {?}
 */
get isRTL(): boolean {
    if (this._dir) {
      return this._dir.dir === 'rtl';
    }
    return false;
  }
/**
 * @param {?} _changeDetectorRef
 * @param {?} _dir
 */
constructor(private _changeDetectorRef: ChangeDetectorRef,
private _dir: Dir) {
  }
/**
 * Refreshes json-formatter and rerenders [data]
 * @return {?}
 */
refresh(): void {
    this._changeDetectorRef.markForCheck();
  }
/**
 * Toggles collapse/expanded state of component.
 * @return {?}
 */
toggle(): void {
    this._open = !this._open;
  }
/**
 * @return {?}
 */
isObject(): boolean {
    return this.getType(this._data) === 'object';
  }
/**
 * @return {?}
 */
isArray(): boolean {
    return Array.isArray(this._data);
  }
/**
 * @return {?}
 */
hasChildren(): boolean {
    return this._children && this._children.length > 0;
  }
/**
 * Gets parsed value depending on value type.
 * @param {?} value
 * @return {?}
 */
getValue(value: any): string {
    let /** @type {?} */ type: string = this.getType(value);
    if (type === 'undefined' || (type === 'null')) {
      return type;
    } else if (type === 'date') {
      value = new Date(value).toString();
    } else if (type === 'string') {
      value = '"' + value + '"';
    } else if (type === 'function') {
      // Remove content of the function
      return value.toString()
          .replace(/[\r\n]/g, '')
          .replace(/\{.*\}/, '') + '{…}';
    }
    return value;
  }
/**
 * Gets type of object.
 * returns 'null' if object is null and 'date' if value is object and can be parsed to a date.
 * @param {?} object
 * @return {?}
 */
getType(object: any): string {
    if (typeof object === 'object') {
      if (!object) {
        return 'null';
      }
      let /** @type {?} */ date: Date = new Date(object);
      if (Object.prototype.toString.call(date) === '[object Date]') {
        if (!Number.isNaN(date.getTime())) {
          return 'date';
        }
      }
    }
    return typeof object;
  }
/**
 * Generates string representation depending if its an object or function.
 * see: http://stackoverflow.com/a/332429
 * @return {?}
 */
getObjectName(): string {
    let /** @type {?} */ object: any = this._data;
    if (this.isObject() && !object.constructor) {
        return 'Object';
    }
    let /** @type {?} */ funcNameRegex: RegExp = /function (.{1,})\(/;
    let /** @type {?} */ results: RegExpExecArray = (funcNameRegex).exec((object).constructor.toString());
    if (results && results.length > 1) {
      return results[1];
    } else {
      return '';
    }
  }
/**
 * Creates preview of nodes children to render in tooltip depending if its an array or an object.
 * @return {?}
 */
getPreview(): string {
    let /** @type {?} */ previewData: string[];
    let /** @type {?} */ startChar: string = '{ ';
    let /** @type {?} */ endChar: string = ' }';
    if (this.isArray()) {
      let /** @type {?} */ previewArray: any[] = this._data.slice(0, TdJsonFormatterComponent.PREVIEW_LIMIT);
      previewData = previewArray.map((obj: any) => {
        return this.getValue(obj);
      });
      startChar = '[';
      endChar = ']';
    } else {
      let /** @type {?} */ previewKeys: string[] = this._children.slice(0, TdJsonFormatterComponent.PREVIEW_LIMIT);
      previewData = previewKeys.map((key: string) => {
        return key + ': ' + this.getValue(this._data[key]);
      });
    }
    let /** @type {?} */ previewString: string =  previewData.join(', ');
    let /** @type {?} */ ellipsis: string = previewData.length >= TdJsonFormatterComponent.PREVIEW_LIMIT ||
                           previewString.length > TdJsonFormatterComponent.PREVIEW_STRING_MAX_LENGTH ? '…' : '';
    return startChar + previewString.substring(0, TdJsonFormatterComponent.PREVIEW_STRING_MAX_LENGTH) +
           ellipsis + endChar;
  }
/**
 * @return {?}
 */
private parseChildren(): void {
    if (this.isObject()) {
      this._children = [];
      for (let /** @type {?} */ key in this._data) {
        this._children.push(key);
      }
    }
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'td-json-formatter',
  styleUrls: ['./json-formatter.component.css' ],
  templateUrl: './json-formatter.component.html',
  animations: [
    TdCollapseAnimation(),
  ],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
{type: Dir, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'levelsOpen': [{ type: Input, args: ['levelsOpen', ] },],
'key': [{ type: Input, args: ['key', ] },],
'data': [{ type: Input, args: ['data', ] },],
};
}

function TdJsonFormatterComponent_tsickle_Closure_declarations() {
/**
 * Max length for property names. Any names bigger than this get trunctated.
 * @type {?}
 */
TdJsonFormatterComponent.KEY_MAX_LENGTH;
/**
 * Max length for preview string. Any names bigger than this get trunctated.
 * @type {?}
 */
TdJsonFormatterComponent.PREVIEW_STRING_MAX_LENGTH;
/**
 * Max tooltip preview elements.
 * @type {?}
 */
TdJsonFormatterComponent.PREVIEW_LIMIT;
/** @type {?} */
TdJsonFormatterComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TdJsonFormatterComponent.ctorParameters;
/** @type {?} */
TdJsonFormatterComponent.propDecorators;
/** @type {?} */
TdJsonFormatterComponent.prototype._key;
/** @type {?} */
TdJsonFormatterComponent.prototype._data;
/** @type {?} */
TdJsonFormatterComponent.prototype._children;
/** @type {?} */
TdJsonFormatterComponent.prototype._open;
/** @type {?} */
TdJsonFormatterComponent.prototype._levelsOpen;
/** @type {?} */
TdJsonFormatterComponent.prototype._changeDetectorRef;
/** @type {?} */
TdJsonFormatterComponent.prototype._dir;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
